import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const compact = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (Math.abs(v) >= 1000) return `₹${Math.round(v / 1000)}k`;
  return `₹${v}`;
};

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const over = d.cumulative_actual - d.cumulative_budget;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-lg text-xs space-y-1" data-testid="trend-tooltip">
      <div className="font-semibold text-slate-900">{label}</div>
      <div className="flex justify-between gap-6"><span className="text-slate-500">Spent this month</span><span className="font-medium">{formatCurrency(d.actual)}</span></div>
      <div className="flex justify-between gap-6"><span className="text-slate-500">Budget pace</span><span className="font-medium">{formatCurrency(d.budget)}</span></div>
      <div className="flex justify-between gap-6 pt-1 border-t border-slate-100"><span className="text-slate-500">Spent to date</span><span className="font-medium">{formatCurrency(d.cumulative_actual)}</span></div>
      <div className="flex justify-between gap-6"><span className="text-slate-500">Budget to date</span><span className="font-medium">{formatCurrency(d.cumulative_budget)}</span></div>
      <div className="flex justify-between gap-6">
        <span className="text-slate-500">{over > 0 ? "Ahead of budget" : "Under budget"}</span>
        <span className={over > 0 ? "font-medium text-red-600" : "font-medium text-emerald-600"}>{formatCurrency(Math.abs(over))}</span>
      </div>
    </div>
  );
}

export default function CostTrendChart({ jobs }) {
  const navigate = useNavigate();
  const [months, setMonths] = useState(6);
  const [jobId, setJobId] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["cost-trend", months, jobId],
    queryFn: async () =>
      (await api.get(`/dashboard/cost-trend`, { params: { months, job_id: jobId === "all" ? "" : jobId } })).data.trend,
  });

  const rows = data || [];
  const last = rows[rows.length - 1];
  const drift = last ? last.cumulative_actual - last.cumulative_budget : 0;

  return (
    <div data-testid="cost-trend-chart">
      <div className="flex flex-wrap items-center justify-between gap-3 px-2 pb-3">
        <div className="text-sm text-slate-500">
          {last ? (
            <>
              Spent to date <span className="font-semibold text-slate-900">{formatCurrency(last.cumulative_actual)}</span> vs budget pace{" "}
              <span className="font-semibold text-slate-900">{formatCurrency(last.cumulative_budget)}</span> ·{" "}
              <span className={drift > 0 ? "font-semibold text-red-600" : "font-semibold text-emerald-600"} data-testid="trend-drift">
                {drift > 0 ? `${formatCurrency(drift)} ahead of plan` : `${formatCurrency(Math.abs(drift))} under plan`}
              </span>
            </>
          ) : "No data"}
        </div>
        <div className="flex items-center gap-2">
          <Select value={jobId} onValueChange={setJobId}>
            <SelectTrigger className="h-8 w-[190px] text-xs" data-testid="trend-job-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All jobs</SelectItem>
              {(jobs || []).map((j) => (
                <SelectItem key={j.id} value={j.id}>{j.code} · {j.job_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
            {[6, 12].map((m) => (
              <Button key={m} size="sm" variant="ghost" onClick={() => setMonths(m)}
                className={`h-7 px-3 text-xs ${months === m ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
                data-testid={`trend-months-${m}`}>
                {m}M
              </Button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="h-[300px] flex items-center justify-center text-slate-400 text-sm">Loading trend...</div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart
            data={rows}
            margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
            onClick={() => jobId !== "all" && navigate(`/jobs/${jobId}`)}
            className={jobId !== "all" ? "cursor-pointer" : ""}
          >
            <defs>
              <linearGradient id="spendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#dc2626" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#dc2626" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} stroke="#94a3b8" width={62} />
            <Tooltip content={<Tip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="cumulative_actual" name="Spent to date" stroke="#dc2626" strokeWidth={2} fill="url(#spendFill)" dot={{ r: 3 }} />
            <Line type="monotone" dataKey="cumulative_budget" name="Budget pace to date" stroke="#2563eb" strokeWidth={2} strokeDasharray="5 4" dot={false} />
            <Line type="monotone" dataKey="actual" name="Spent in month" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
      <p className="px-2 pt-2 text-xs text-slate-400">
        When the red "spent to date" line rises above the dashed budget pace line, spending is running ahead of plan.
        {jobId !== "all" && " Click the chart to open this job's detail page."}
      </p>
    </div>
  );
}
